import React, { useMemo, useRef, useState } from "react"
import { Collapse, ColorPicker, Drawer, Select } from "antd"
import type { ColorPickerProps } from "antd"
import TableStyleControl from "shared/ui/TableStyle/TableStyleControl.component"
import { paletteColours } from "shared/ui/Charts/palettes"
import {
  DASHBOARD_TABLE_STYLE,
  Page,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"
import {
  TileRuntime,
  runtimeModel,
  selectActivePage,
  useDashboardStore,
} from "features/Dashboard/runtime/dashboardStore"
import { leafLabel } from "features/Dashboard/runtime/modelAdapters"
import { numericVariables } from "features/Dashboard/runtime/prepScript"
import ScriptEditor from "features/Dashboard/components/Inspector/ScriptEditor.component"
import PreparationField from "./PreparationField.component"

/**
 * The preparation script editor. It holds its own text from the moment the
 * drawer opens (the drawer drops its content when hidden, so each opening
 * starts from the document) and reports every edit through `onDraft`.
 */
const PrepScriptField: React.FC<{
  initial: string
  onDraft: (text: string) => void
  onApply: () => void
}> = ({ initial, onDraft, onApply }) => {
  const [text, setText] = useState(initial)
  return (
    <div className="dq-field">
      <ScriptEditor
        value={text}
        onChange={(next) => {
          setText(next)
          onDraft(next)
        }}
        onRun={onApply}
        prefixPreview=""
        runLabel="Apply"
        ariaLabel="Preparation script"
        placeholder="Commands run on the data after the pipelines and the weight, before any tile."
      />
      <span className="dq-field__hint">
        For what a pipeline cannot do. Applying it prepares the data again and
        re-runs every tile.
      </span>
    </div>
  )
}

/** The select's value for no weight (a select cannot hold null). */
const NO_WEIGHT = "__none__"

/**
 * The dashboard weight: a numeric variable every tile is weighted by, or
 * none. A saved weight the codebook no longer lists stays selected with a
 * "not found" chip. A change prepares the data again.
 */
const WeightingField: React.FC = () => {
  const codebook = useDashboardStore((s) => s.codebook)
  const data = useDashboardStore((s) => s.document.data)
  const setData = useDashboardStore((s) => s.setData)
  const numeric = useMemo(() => numericVariables(codebook), [codebook])
  const weight = data.weight
  const listed = weight !== null && numeric.includes(weight)
  const missing = weight !== null && codebook !== null && !listed
  const options = [
    { value: NO_WEIGHT, label: "None" },
    ...(weight !== null && !listed ? [{ value: weight, label: weight }] : []),
    ...numeric.map((v) => ({ value: v, label: v })),
  ]
  return (
    <section className="dq-data__part" aria-labelledby="dq-data-weight">
      <h3 id="dq-data-weight" className="dq-data__title">
        Weighting
      </h3>
      <div className="dq-data__weight">
        <Select
          showSearch
          size="small"
          aria-label="Weight"
          value={weight ?? NO_WEIGHT}
          options={options}
          optionFilterProp="label"
          notFoundContent="No numeric variable matches"
          onChange={(v: string) => {
            const next = v === NO_WEIGHT ? null : v
            if (next !== weight) setData({ ...data, weight: next })
          }}
        />
        {missing && <span className="dq-data__missing">not found</span>}
      </div>
      <p className="dq-field__hint">
        Every tile is weighted by it. A tile can leave it out with Unweighted in
        its Data tab.
      </p>
    </section>
  )
}

/**
 * The labels a dashboard colour can name on the active page: the
 * categories of its tiles' last results (both axes, each label once,
 * Totals last; a repeated tile's first panel), its tiles' measures, then
 * any label that already has a colour, so it can be cleared.
 */
const colourLabels = (
  page: Page | undefined,
  runtimes: Record<string, TileRuntime>,
  coloured: Record<string, string>
): string[] => {
  const labels = new Set<string>()
  const totals = new Set<string>()
  page?.tiles.forEach((tile) => {
    const model = runtimeModel(runtimes[tile.id])
    if (!model) return
    ;[...model.down.leaves, ...model.across.leaves].forEach((leaf) =>
      (leaf.isTotal ? totals : labels).add(leafLabel(leaf))
    )
  })
  totals.forEach((l) => labels.add(l))
  page?.tiles.forEach((tile) => {
    if (isDataTile(tile)) tile.measures.forEach((m) => labels.add(m.label))
  })
  Object.keys(coloured).forEach((l) => labels.add(l))
  labels.delete("")
  return [...labels]
}

/** A picked colour as `#rrggbb`; null when cleared or not a plain colour. */
const pickedHex = (
  colour: Parameters<NonNullable<ColorPickerProps["onChangeComplete"]>>[0]
): string | null => {
  if (colour.cleared) return null
  const hex = colour.toHexString().slice(0, 7).toLowerCase()
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : null
}

/**
 * One swatch per category or measure label. A colour set here follows the
 * label onto every chart of the dashboard and into the PowerPoint export:
 * a series' colour, or on a single-series bar chart the colour of the
 * category's bar; clearing it gives the label back its palette colour.
 * Colours are display only, so no tile runs again.
 */
const ColoursField: React.FC = () => {
  const theme = useDashboardStore((s) => s.document.theme)
  const setTheme = useDashboardStore((s) => s.setTheme)
  const page = useDashboardStore(selectActivePage)
  const runtimes = useDashboardStore((s) => s.tiles)
  const coloured = theme.categoryColours
  const labels = useMemo(
    () => colourLabels(page, runtimes, coloured),
    [page, runtimes, coloured]
  )
  const presets = useMemo(
    () => [{ label: "Palette", colors: paletteColours(theme.chartPalette, 8) }],
    [theme.chartPalette]
  )
  const setColour = (label: string, colour: string | null): void => {
    const current = Object.hasOwn(coloured, label) ? coloured[label] : null
    if (current === colour) return
    const next = Object.fromEntries(
      Object.entries(coloured).filter(([l]) => l !== label)
    )
    setTheme({
      ...theme,
      categoryColours: colour ? { ...next, [label]: colour } : next,
    })
  }

  return (
    <section className="dq-data__part" aria-labelledby="dq-data-colours">
      <h3 id="dq-data-colours" className="dq-data__title">
        Colours
      </h3>
      {labels.length === 0 ? (
        <p className="dq-field__hint">
          Run the tiles on this page to list their categories.
        </p>
      ) : (
        <ul className="dq-colours" aria-label="Colours">
          {labels.map((label) => {
            const colour = Object.hasOwn(coloured, label)
              ? coloured[label]
              : null
            return (
              <li key={label} className="dq-colours__row">
                <ColorPicker
                  size="small"
                  value={colour}
                  allowClear
                  disabledAlpha
                  format="hex"
                  presets={presets}
                  onChangeComplete={(c) => setColour(label, pickedHex(c))}
                  onClear={() => setColour(label, null)}
                >
                  <button
                    type="button"
                    className="dq-colours__swatch"
                    aria-label={`Colour for ${label}`}
                    title={colour ?? "Palette colour"}
                    data-set={colour !== null}
                    style={colour ? { background: colour } : undefined}
                  />
                </ColorPicker>
                <span className="dq-colours__label" title={label}>
                  {label}
                </span>
              </li>
            )
          })}
        </ul>
      )}
      <p className="dq-field__hint">
        A colour follows its category or measure onto every chart. On a
        multi-series bar the series colours apply.
      </p>
    </section>
  )
}

/**
 * The dashboard's Data panel in Build mode: the saved pipelines that
 * prepare the data, the weight, the colours of categories and measures,
 * the table style every table follows unless a tile picks its own, and the
 * hand-written preparation script under Advanced. Pipelines, weight and
 * script change what runs, so each change prepares the data again; colours
 * and table style only change how results are drawn. A script edit is
 * written on Apply or when the panel closes, never per keystroke. The panel
 * is open while the store says so, so the Inspector can open it too.
 */
const DataPanel: React.FC<{ getContainer: () => HTMLElement }> = ({
  getContainer,
}) => {
  const open = useDashboardStore((s) => s.dataPanelOpen)
  const setOpen = useDashboardStore((s) => s.setDataPanelOpen)
  const prepScript = useDashboardStore((s) => s.document.prepScript)
  const theme = useDashboardStore((s) => s.document.theme)
  const setPrepScript = useDashboardStore((s) => s.setPrepScript)
  const setTheme = useDashboardStore((s) => s.setTheme)
  /** The script as edited since the panel opened; null before any edit. */
  const draft = useRef<string | null>(null)

  const apply = (): void => {
    if (draft.current !== null && draft.current !== prepScript) {
      setPrepScript(draft.current)
    }
  }
  const close = (): void => {
    apply()
    draft.current = null
    setOpen(false)
  }

  return (
    <Drawer
      open={open}
      onClose={close}
      getContainer={getContainer}
      rootClassName="dq-data-drawer"
      placement="right"
      title="Data"
      width={440}
      destroyOnHidden
    >
      <div className="dq-data">
        <PreparationField beforeLeave={close} />
        <WeightingField />
        <ColoursField />
        {/* Not <label>: the control holds a picker and a button. */}
        <section className="dq-data__part" aria-labelledby="dq-data-table">
          <h3 id="dq-data-table" className="dq-data__title">
            Table style
          </h3>
          <TableStyleControl
            value={theme.tableStyle ?? DASHBOARD_TABLE_STYLE}
            onChange={(tableStyle) => setTheme({ ...theme, tableStyle })}
            drawerTitle="Table style: dashboard default"
          />
          <p className="dq-field__hint">
            Tables use it unless a tile picks its own.
          </p>
        </section>
        <Collapse
          size="small"
          ghost
          className="dq-data__advanced"
          // A dashboard that already has a script shows it.
          defaultActiveKey={prepScript.trim() ? ["advanced"] : []}
          items={[
            {
              key: "advanced",
              label: "Advanced",
              children: (
                <PrepScriptField
                  initial={prepScript}
                  onDraft={(text) => {
                    draft.current = text
                  }}
                  onApply={apply}
                />
              ),
            },
          ]}
        />
      </div>
    </Drawer>
  )
}

export default DataPanel
